// @flow
// Userlayers component manager

import React from 'react'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles/index'
import { ids as uiIds, MapToolbar, MapToolbarPanel, styles as uiStyles } from 'catena/er2_ui'
import ErrorBoundary from 'catena/er2_map_userlayers/js/components/error_boundary'
import MapBaseLayers from 'catena/er2_map_baselayers/src/baselayers'
import Services from 'catena/er2_map_services/js/components/services'
import Timeseries from 'catena/er2_map_timeseries/js/components/timeseries'
import type { GeoBarType } from 'catena/er2_ui'
import CatenaMapToolbar from 'catena/er2_map/src/maptoolbar'
import type { ServicesType } from 'catena/er2_map_services/js/reducers/services'
import type { IdentifyType } from 'catena/er2_map_userlayers/js/reducers/identify'
import LayerPanelStyles from 'catena/er2_styles/layers'
import { getMapserverHost, mapToolbarHasActivePanels } from '../utils'
import type { MapBaseLayerType } from '../reducers/mapbaselayer'
import type { MapsourcesType } from '../reducers/mapsources'
import type { MapType } from '../reducers/map'
import type { SitesType } from '../reducers/public_sites'
import MapsourcesContainer from './mapsources'

type UserLayersPropsType = {
    actions: {
        onChangeBaseLayer: Function,
        onChangeMapScale: Function,
        showMapToolbar: Function,
    },
    baseImageURL: string,
    er2Map: Map,
    geoBar: GeoBarType,
    identify: IdentifyType,
    map: MapType,
    mapBaseLayer: MapBaseLayerType,
    mapsources: MapsourcesType,
    mapToolbar: {
        items: [],
    },
    mapToolbarButtons?: [],
    publicSites: SitesType,
    services: ServicesType,
    theme: LayerPanelStyles,
    token: {
        value: string,
    }
}

class UserLayersContainer extends React.Component<UserLayersPropsType> {
    static defaultProps = {
        mapToolbarButtons: undefined,
    }

    render() {
        let scales = [24000, 100000, 250000, 1000000, 25000000]
        if (scales.indexOf(this.props.map.mapView.mapScale) < 0) {
            scales = [this.props.map.mapView.mapScale, ...scales]
        }

        const activePanels = this.props.mapToolbar.items.filter(item => item.active)
        const panels = activePanels.map(item => (
            <MapToolbarPanel
                key={item.id}
                geoBar={this.props.geoBar}
                mapsources={this.props.mapsources}
                mapToolbar={this.props.mapToolbar}
                olMap={this.props.er2Map.olmap}
                theme={this.props.theme}
                token={this.props.token}
                actions={this.props.actions}
            >
                {item.id === uiIds.SELECT_BASE_LAYERS && (
                    <div className={this.props.theme.mapToolbarTop}>
                        <h3 className={this.props.theme.mapToolbarTitleBar}>Map Settings</h3>
                        <div style={{ marginLeft: '20px', width: '260px' }}>
                            <div
                                style={{
                                    textAlign: 'left',
                                    marginTop: '10px',
                                    marginBottom: '30px',
                                }}
                            >
                                <Typography color="secondary">Base Layers</Typography>
                            </div>

                            <MapBaseLayers
                                baseImageURL={this.props.baseImageURL}
                                baseLayer={this.props.mapBaseLayer.baseLayer}
                                baseLayerChoices={this.props.mapBaseLayer.baseLayerChoices}
                                keys={this.props.map.apiKeys}
                                onSetBaseLayer={this.props.actions.onChangeBaseLayer}
                            />

                            <Typography color="secondary" style={{ marginTop: '20px' }}>Map Options</Typography>
                            <table style={{ marginTop: '20px', marginLeft: '20px' }}><tbody><tr><td>
                                <Typography
                                    htmlFor="mapscale"
                                    variant="body2"
                                >Map Scale
                                </Typography>
                            </td><td>
                                <Select
                                    onChange={e => this.props.actions.onChangeMapScale(e.target.value)}
                                    value={this.props.map.mapView.mapScale}
                                    inputProps={{
                                        name: 'mapscale',
                                        id: 'mapscale',
                                    }}
                                >
                                    {scales.map(s => (
                                        <MenuItem key={s} value={s}>1 : {s.toLocaleString()}</MenuItem>))}
                                </Select>
                            </td></tr>
                            <tr><td>
                                <Typography variant="body2" style={{ display: 'inline-block' }}>Show Map
                                    Toolbar</Typography>
                            </td><td>
                                <Switch
                                    checked={this.props.map.showMapToolbar}
                                    color="secondary"
                                    onChange={() => this.props.actions.showMapToolbar(!this.props.map.showMapToolbar)}
                                />
                            </td></tr></tbody></table>
                        </div>
                    </div>
                )}
                {item.id === uiIds.SELECT_USER_LAYERS && (
                    <div className={this.props.theme.mapToolbarTop}>
                        <h3 className={this.props.theme.mapToolbarTitleBar}>Layers</h3>
                        <MapsourcesContainer
                            active
                            baseImageURL={this.props.baseImageURL}
                            er2Map={this.props.er2Map}
                            key={'mapsource_layer'}
                            map={this.props.map}
                            mapserverHost={getMapserverHost()}
                            mapsources={this.props.mapsources}
                            mask={'layers'}
                            publicSites={this.props.publicSites}
                            theme={this.props.theme}
                            {...this.props.actions}
                        />
                    </div>
                )}
                {item.id === uiIds.SELECT_USER_TABLES && (
                    <div className={this.props.theme.mapToolbarTop}>
                        <h3 className={this.props.theme.mapToolbarTitleBar}>Tables</h3>
                        <MapsourcesContainer
                            active
                            baseImageURL={this.props.baseImageURL}
                            er2Map={this.props.er2Map}
                            key={'mapsource_table'}
                            map={this.props.map}
                            mask={'tables'}
                            mapserverHost={getMapserverHost()}
                            mapsources={this.props.mapsources}
                            publicSites={this.props.publicSites}
                            theme={this.props.theme}
                            {...this.props.actions}
                        />
                    </div>
                )}
                {item.id === uiIds.SELECT_SERVICES && (
                    <Services
                        active
                        key={'data_services_bar'}
                        mapsources={this.props.mapsources}
                        services={this.props.services}
                        theme={this.props.theme}
                        token={this.props.token}
                        {...this.props.actions}
                    />
                )}
                {item.id === uiIds.SELECT_TIMESERIES && (
                    <Timeseries
                        active
                        key={'data_timeseries_bar'}
                        timseries={this.props.timeseries}
                        theme={this.props.theme}
                        token={this.props.token}
                        {...this.props.actions}
                    />
                )}
            </MapToolbarPanel>
        ))

        if (!panels.length && this.props.er2Map) {
            panels.push((
                <MapsourcesContainer
                    active={false}
                    er2Map={this.props.er2Map}
                    key={'mapsource'}
                    map={this.props.map}
                    mapserverHost={getMapserverHost()}
                    mapsources={this.props.mapsources}
                    publicSites={this.props.publicSites}
                    {...this.props.actions}
                />
            ))
        }

        const fontSize = 14
        const muiTheme = createMuiTheme({
            typography: {
                fontFamily: '\'Roboto Condensed\', sans-serif',
                useNextVariants: true,
            },
            palette: {
                secondary: {
                    main: '#12A4B6',
                    contrastText: '#fff',
                },
            },
            overrides: {
                MuiExpansionPanelDetails: {
                    root: {
                        padding: 0,
                    },
                },
                MuiExpansionPanelSummary: {
                    content: {
                        margin: '0 0 0 0',
                        '&$expanded': {
                            margin: '0 0 0 0',
                        },
                    },
                    root: {
                        // Fixes increase in height when opened
                        minHeight: '48px',
                        '&$expanded': {
                            margin: '0 0 0 0',
                            minHeight: '48px',
                        },
                    },
                },
                MuiInput: {
                    input: {
                        fontSize,
                        padding: '0px',
                    },
                },
                MuiInputLabel: {
                    formControl: {
                        transform: 'translate(0, 14px) scale(1)',
                    },
                },
                MuiMenuItem: {
                    root: {
                        fontSize,
                    },
                },
                MuiPaper: {
                    elevation1: {
                        boxShadow: 'initial',
                        // border: '1px solid gray',
                    },
                },
            },
        })

        return (
            <MuiThemeProvider theme={muiTheme}><ErrorBoundary>
                {panels}
                {this.props.map.showMapToolbar && (
                    <CatenaMapToolbar
                        buttons={this.props.mapToolbarButtons}
                        identify={this.props.identify}
                        mapToolbarPanelClosed={!mapToolbarHasActivePanels(this.props.mapToolbar)}
                        map={this.props.er2Map}
                        onIdentify={this.props.actions.onIdentify}
                        onIdentifyHandled={this.props.actions.onIdentifyHandled}
                        onMapToolbarButtonClicked={(bname) => {
                            console.warn(`button ${bname} clicked`)
                        }}
                        theme={this.props.theme}
                    />)
                }
                <MapToolbar
                    geoBar={this.props.geoBar}
                    mapToolbar={this.props.mapToolbar}
                    theme={this.props.theme}
                    {...this.props.actions}
                />
            </ErrorBoundary></MuiThemeProvider>
        )
    }
}

export default UserLayersContainer
